import React from "react";
import { LeftIcon, RightIcon } from "assets/icons";
import PaginationItem from "./PaginationItem";
import { Div } from "./styles";

/* 타입 */
interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  currentPage: number;
  lastPage: number;
  onClickPageButton?: (page: number) => void;
  /** 현재 페이지 양옆 표시 개수 (기본 1) */
  siblingCount?: number;
  /** 양 끝(맨앞/맨뒤) 표시 개수 (기본 1) */
  boundaryCount?: number;
}

/* 유틸 */
const range = (start: number, end: number): number[] =>
  start > end
    ? []
    : Array.from({ length: end - start + 1 }, (_, i) => start + i);

type PaginationItemType = number | "start-ellipsis" | "end-ellipsis";

/**
 * @description 현재 페이지를 항상 윈도우 중앙에 두는 슬라이딩 윈도우(MUI식) 항목 배열을 생성.
 * 양끝(start/end)을 clamp 하므로 페이지 수가 늘어도 윈도우 폭은 고정 → 끝쪽 꼬리가 길어지지 않음.
 */
const getPaginationItems = (
  currentPage: number,
  lastPage: number,
  siblingCount: number,
  boundaryCount: number,
): PaginationItemType[] => {
  if (lastPage < 1) return [];

  const startPages = range(1, Math.min(boundaryCount, lastPage));
  const endPages = range(
    Math.max(lastPage - boundaryCount + 1, boundaryCount + 1),
    lastPage,
  );

  // 양쪽 모두 clamp → 윈도우 폭 고정 (끝쪽에서 꼬리 무한 증가 방지)
  const siblingsStart = Math.max(
    Math.min(
      currentPage - siblingCount,
      lastPage - boundaryCount - siblingCount * 2 - 1,
    ),
    boundaryCount + 2,
  );
  const siblingsEnd = Math.min(
    Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
    endPages.length > 0 ? endPages[0] - 2 : lastPage - 1,
  );

  return [
    ...startPages,
    // 좌측 갭: 2칸 이상이면 …, 정확히 1칸이면 생략 없이 그 번호 노출
    ...(siblingsStart > boundaryCount + 2
      ? (["start-ellipsis"] as const)
      : boundaryCount + 1 < lastPage - boundaryCount
        ? [boundaryCount + 1]
        : []),
    ...range(siblingsStart, siblingsEnd),
    // 우측 갭: 동일 규칙
    ...(siblingsEnd < lastPage - boundaryCount - 1
      ? (["end-ellipsis"] as const)
      : lastPage - boundaryCount > boundaryCount
        ? [lastPage - boundaryCount]
        : []),
    ...endPages,
  ];
};

/* 컴포넌트 */
const Pagination = ({
  currentPage: currentPageProp,
  lastPage: lastPageProp,
  onClickPageButton,
  siblingCount = 1,
  boundaryCount = 1,
  ...rest
}: PaginationProps) => {
  // URL searchParams 등에서 문자열로 넘어올 수 있어 숫자로 강제 변환
  // (특히 currentPage + siblingCount 가 문자열 연결되면 우측 윈도우 clamp가 깨짐)
  const currentPage = Number(currentPageProp);
  const lastPage = Number(lastPageProp);

  const prevPage = currentPage - 1;
  const nextPage = currentPage + 1;

  const items = getPaginationItems(
    currentPage,
    lastPage,
    siblingCount,
    boundaryCount,
  );

  const clampPage = (page: number) => Math.min(Math.max(page, 1), lastPage);

  const handleClickPaginationItem = (page: number) => {
    if (onClickPageButton) onClickPageButton(clampPage(page));
  };

  // … 클릭 시 한 그룹(siblingCount*2 + 1) 만큼 점프
  const groupJump = siblingCount * 2 + 1;

  return (
    <Div {...rest}>
      <PaginationItem
        disabled={prevPage < 1}
        onClick={() => handleClickPaginationItem(prevPage)}
      >
        <LeftIcon />
      </PaginationItem>

      {items.map((item, index) => {
        if (item === "start-ellipsis" || item === "end-ellipsis") {
          const target =
            item === "start-ellipsis"
              ? currentPage - groupJump
              : currentPage + groupJump;

          return (
            <PaginationItem
              key={item}
              onClick={() => handleClickPaginationItem(target)}
            >
              ···
            </PaginationItem>
          );
        }

        return (
          <PaginationItem
            key={`page-${item}-${index}`}
            selected={item === currentPage}
            onClick={() => handleClickPaginationItem(item)}
          >
            {item}
          </PaginationItem>
        );
      })}

      <PaginationItem
        disabled={nextPage > lastPage}
        onClick={() => handleClickPaginationItem(nextPage)}
      >
        <RightIcon />
      </PaginationItem>
    </Div>
  );
};

export default Pagination;
